'use client';

import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { useCalendarStore } from '@/stores/calendar-store';
import { useCommandPalette } from './useCommandPalette';
import { useRecentSearches } from './useRecentSearches';
import { useGlobalShortcuts } from '@/hooks/useGlobalShortcuts';
import { CommandPaletteInput } from './CommandPaletteInput';
import { CommandPaletteFilters } from './CommandPaletteFilters';
import { CommandPaletteResults } from './CommandPaletteResults';
import { CommandPaletteEmpty } from './CommandPaletteEmpty';
import { uiFb } from '@/lib/i18n';
import type { SearchResult } from '@/lib/search-client';

export function CommandPalette() {
  useGlobalShortcuts();

  const {
    isOpen,
    query,
    setQuery,
    activeFilter,
    setActiveFilter,
    activeIndex,
    setActiveIndex,
    focusZone,
    setFocusZone,
    results,
    isLoading,
    shardError,
    searchSource,
    correctedQuery,
    close,
    handleKeyDown: hookHandleKeyDown,
    retryShard,
    pendingAiResults,
    acceptAiResults,
    dismissAiResults,
    forceOriginalQuery,
  } = useCommandPalette();

  const lang = useCalendarStore((s) => s.lang);
  const router = useRouter();
  const { items: recentSearches, add: addRecent, clear: clearRecent } = useRecentSearches(lang);

  const navigate = useCallback(
    (href: string, result?: SearchResult) => {
      if (result) {
        addRecent({ query, href: result.href, title: result.title });
      }
      close();
      router.push(href);
    },
    [close, addRecent, query, router],
  );

  const handleSelect = useCallback(
    (result: SearchResult) => navigate(result.href, result),
    [navigate],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const result = hookHandleKeyDown(e);
      if (result && e.key === 'Enter') {
        navigate(result.href, result);
      }
    },
    [hookHandleKeyDown, navigate],
  );

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const showResults = query.length >= 2 && results.length > 0;
  const showEmpty = !showResults;

  const dialog = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-[var(--overlay-scrim,rgba(0,0,0,0.5))]"
        onClick={close}
        aria-hidden="true"
      />

      {/* Dialog shell — responsive: centered on desktop, full-screen on mobile */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="command-palette-title"
        className="fixed inset-0 z-50 sm:inset-auto sm:left-1/2 sm:top-[15%] sm:w-[90vw] sm:max-w-[560px] sm:-translate-x-1/2 flex flex-col overflow-hidden sm:rounded-2xl border border-border-primary shadow-2xl glass-panel"
        onKeyDown={handleKeyDown}
      >
        <h2 id="command-palette-title" className="sr-only">
          {uiFb('ui.search.title', 'Search', lang)}
        </h2>

        <CommandPaletteInput
          query={query}
          onQueryChange={(q) => {
            setQuery(q);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
          isFocused={focusZone === 'input'}
          onFocus={() => setFocusZone('input')}
          resultCount={results.length}
          activeDescendant={
            focusZone === 'results' && results[activeIndex]
              ? `search-result-${activeIndex}`
              : undefined
          }
          onClose={close}
          lang={lang}
        />

        <CommandPaletteFilters
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          isFocused={focusZone === 'filters'}
          onFocus={() => setFocusZone('filters')}
          lang={lang}
        />

        <div id="command-palette-results-container" className="flex-1 overflow-hidden">
          {pendingAiResults && showResults && (
            <div className="flex items-center gap-2 border-b border-border-subtle bg-surface-secondary/50 px-4 py-2">
              <span className="text-sm text-accent-primary font-medium">{uiFb('ui.search.ai_badge', 'AI', lang)}</span>
              <span className="text-sm text-text-secondary">
                {uiFb('ui.search.better_results', 'Better results found', lang)}
              </span>
              <div className="ms-auto flex gap-2">
                <button
                  onClick={acceptAiResults}
                  className="rounded-md bg-brand px-2.5 py-1 min-h-[44px] text-label font-medium text-text-on-brand transition-colors hover:bg-brand/90"
                >
                  {uiFb('ui.search.show_ai', 'Show', lang)}
                </button>
                <button
                  onClick={dismissAiResults}
                  className="rounded-md px-2 py-1 min-h-[44px] text-label text-text-dim transition-colors hover:text-text-secondary"
                >
                  {uiFb('ui.search.dismiss', 'Dismiss', lang)}
                </button>
              </div>
            </div>
          )}

          {correctedQuery && showResults && (
            <div className="px-4 py-2 text-sm border-b border-border-subtle">
              <span className="text-text-dim">{uiFb('ui.search.showing_results_for', 'Showing results for', lang)}</span>{' '}
              <span className="font-medium text-text-primary">{correctedQuery}</span>
              <button
                className="ms-2 text-accent-primary hover:underline text-sm min-h-[44px] inline-flex items-center"
                onClick={forceOriginalQuery}
              >
                {uiFb('ui.search.search_instead_for', 'Search instead for:', lang)} {query}
              </button>
            </div>
          )}

          {showResults && (
            <CommandPaletteResults
              results={results}
              activeIndex={activeIndex}
              onActiveIndexChange={setActiveIndex}
              onSelect={handleSelect}
              query={query}
              lang={lang}
            />
          )}

          {showEmpty && (
            <CommandPaletteEmpty
              query={query}
              hasResults={results.length > 0}
              isLoading={isLoading}
              shardError={shardError}
              recentSearches={recentSearches}
              onClearRecent={clearRecent}
              onNavigate={(href) => navigate(href)}
              onRetry={retryShard}
              lang={lang}
            />
          )}
        </div>

        {/* Footer hint */}
        {showResults && (
          <div className="flex items-center gap-3 border-t border-border-subtle px-4 py-2 text-label text-text-dim">
            <span>
              <kbd className="rounded border border-border-subtle px-1 py-0.5 text-caption">↑↓</kbd>{' '}
              {uiFb('ui.search.navigate', 'navigate', lang)}
            </span>
            <span>
              <kbd className="rounded border border-border-subtle px-1 py-0.5 text-caption">↵</kbd>{' '}
              {uiFb('ui.search.open', 'open', lang)}
            </span>
            <span className="ms-auto text-caption">
              {searchSource === 'ai' && (
                <span className="me-1 text-accent-primary">{uiFb('ui.search.ai_badge', 'AI', lang)}</span>
              )}
              {uiFb('ui.search.result_count', '{count} results', lang, { count: results.length })}
            </span>
          </div>
        )}
      </div>
    </>
  );

  // Use portal to mount at document body
  if (typeof document === 'undefined') return null;
  return createPortal(dialog, document.body);
}
